/**
 * invoice-select-labels.ts — kody i etykiety list wyboru faktur, które w liście przechodzą przez
 * nadpisania kolumn (`PURCHASE_INVOICE_OVERRIDES`/`SALE_INVOICE_OVERRIDES`): „Typ dokumentu" (rodzaj
 * faktury FA(3)) i „Źródło" (skąd faktura przyszła). Znaczniki KSeF/JPK mają własny plik
 * (`ksef-jpk-markers.ts`).
 *
 * Etykiety żyją w `pl.json` pod prefiksem faktur danej strony (`tenant.purchaseInvoices` /
 * `tenant.saleInvoices` — te same klucze czyta komórka kolumny i plakietka źródła), a TA funkcja jest
 * jedynym miejscem mapowania kod → tekst dla komórki i dla opcji listy wyboru w wierszu filtrów, więc
 * filtr i wiersze mówią to samo. Kod spoza listy (dane historyczne) wraca surowy (`defaultValue`).
 *
 * Rodzaje faktury (`INVOICE_KINDS` z `@saas/shared`) to zamknięty enum walidowany po stronie API,
 * więc lista kodów jest stała; test `document-select-labels.test.ts` pilnuje, że każdy kod ma
 * tłumaczenie w obu prefiksach (klucze budowane z kodu są martwym polem skanera `check:i18n`) — to
 * ŚWIADOMY wyjątek od reguły z `ksef-jpk-markers.ts` (literał `t()` per gałąź); strażnikiem jest
 * właśnie ten test każdego kodu.
 *
 * Plik trzyma też `INVOICE_STATUS_CODES` — kody kolumny „Status" i jej filtra; nazwy statusów:
 * `invoiceStatusLabel` (te same na karcie faktury, w komórce i w filtrze).
 */

import { INVOICE_KINDS, INVOICE_STATUSES } from '@saas/shared';

import {
  codeFilterOptions,
  type CoreColumnTranslateFn,
  type ListFilterOption,
} from '@/lib/ext-list-columns';

/** Prefiks kluczy i18n listy faktur: zakupu albo sprzedaży. */
export type InvoiceLabelPrefix = 'tenant.purchaseInvoices' | 'tenant.saleInvoices';

/**
 * Statusy faktury w kolumnie „Status” i jej filtrze (zakup i sprzedaż): sześć wartości listy „Status”
 * karty (`INVOICE_STATUSES` z `@saas/shared`) + trzy statusy, których karta nie oferuje, a które mają
 * faktury tworzone z KSeF: `booked` (`lib/ksef/automation.ts`, `lib/ksef/create-invoice-document.ts`
 * w API) oraz `new` i `verified` — wartości reguły automatyzacji KSeF „Status po utworzeniu”
 * (`STATUS_OPTIONS` w `KsefAutomationRulesSection`). Dodatkowe trzy istnieją TYLKO na liście — nie
 * należą do dziedziny pola na karcie (stąd poza `@saas/shared`). Słownik „Status faktury zakupu”
 * (5 pozycji, bez `correction`) NIE jest tu źródłem.
 */
export const INVOICE_STATUS_CODES = [...INVOICE_STATUSES, 'booked', 'new', 'verified'] as const;

/** Źródła faktury, które rozróżnia plakietka `SourceBadge`/`SaleSourceBadge` (reszta = „ręcznie"). */
export const INVOICE_SOURCE_CODES = ['manual', 'symfonia', 'ksef'] as const;

/** Etykieta statusu faktury (zakup i sprzedaż, wspólne klucze); kod spoza listy surowo. */
export function invoiceStatusLabel(code: string, t: CoreColumnTranslateFn): string {
  return t(`tenant.documents.shared.invoiceStatusValues.${code}`, { defaultValue: code });
}

/** Etykieta sposobu płatności faktury sprzedaży (lista formularza); kod spoza listy surowo. */
export function invoicePaymentMethodLabel(code: string, t: CoreColumnTranslateFn): string {
  return t(`tenant.documents.shared.paymentMethodValues.${code}`, { defaultValue: code });
}

/** Etykieta rodzaju faktury („Faktura VAT", „Korekta"…); kod spoza listy surowo. */
export function invoiceKindLabel(
  code: string,
  t: CoreColumnTranslateFn,
  labelPrefix: InvoiceLabelPrefix,
): string {
  return t(`${labelPrefix}.invoiceKindValues.${code}`, { defaultValue: code });
}

/** Etykieta źródła faktury („Ręcznie", „Z Symfonii", „Z KSeF"); kod spoza listy surowo. */
export function invoiceSourceLabel(
  code: string,
  t: CoreColumnTranslateFn,
  labelPrefix: InvoiceLabelPrefix,
): string {
  return t(`${labelPrefix}.source.${code}`, { defaultValue: code });
}

/** Opcje listy wyboru „Typ dokumentu" w wierszu filtrów: wartość = kod z bazy, etykieta jak w komórce. */
export function invoiceKindFilterOptions(
  t: CoreColumnTranslateFn,
  labelPrefix: InvoiceLabelPrefix,
): ListFilterOption[] {
  return codeFilterOptions(
    INVOICE_KINDS,
    (code, translate) => invoiceKindLabel(code, translate, labelPrefix),
    t,
  );
}

/** Opcje listy wyboru „Źródło" faktury w wierszu filtrów: wartość = kod z bazy, etykieta jak w plakietce. */
export function invoiceSourceFilterOptions(
  t: CoreColumnTranslateFn,
  labelPrefix: InvoiceLabelPrefix,
): ListFilterOption[] {
  return codeFilterOptions(
    INVOICE_SOURCE_CODES,
    (code, translate) => invoiceSourceLabel(code, translate, labelPrefix),
    t,
  );
}

/** Pola wiersza faktury czytane przez tekst komórek do pliku CSV (wspólne dla obu klas). */
interface InvoiceExportTextRow {
  vendorNameCached?: string | null;
  vendorTaxIdCached?: string | null;
  source?: string | null;
}

/**
 * Tekst komórek faktury z ozdobnikami do pliku CSV (eksport widoku listy, E16) — `textOverrides`
 * dla `useEngineListExport`. „Dostawca"/„Nabywca" = nazwa i NIP jak na ekranie (ze spacją, nie
 * sklejone); „Źródło" = etykieta plakietki (plakietka to komponent z hookiem — bez tego do pliku
 * trafiłby surowy kod), brak źródła = „Ręcznie" jak na plakietce.
 */
export function invoiceExportTextOverrides<Row extends InvoiceExportTextRow>(
  t: CoreColumnTranslateFn,
  labelPrefix: InvoiceLabelPrefix,
): Record<string, (row: Row) => string> {
  return {
    vendor_id: (row) =>
      [row.vendorNameCached ?? '', row.vendorTaxIdCached ? `NIP ${row.vendorTaxIdCached}` : '']
        .filter(Boolean)
        .join(' '),
    source: (row) => invoiceSourceLabel(row.source ?? 'manual', t, labelPrefix),
  };
}
